/**
 * Style interpolation for transitions (UI-18). Easing curves (cubic-bezier presets) + typed value
 * lerp for the animatable subset of StyleDecls. The UIDocument transition engine owns the CURRENT
 * value and drives `t` (0..1, post-easing) from its clock; this module is pure math.
 *
 * Interpolatable value types: numbers (opacity/border-radius/-width), colors (componentwise),
 * `transform` (function-list arg lerp when the lists match structurally), `box-shadow` (field lerp),
 * and plain-px dims (width/height/inset/margins/paddings). Anything else snaps (canInterpolate=false).
 */
import type { Color4, EasingName, ShadowDecl, StyleDecls, TransformDecl, TransformFn } from '../types';

// ---------------------------------------------------------------------------
// easing

/** Cubic-bezier(x1,y1,x2,y2) → time(0..1) ⇒ progress(0..1). Newton + bisection fallback. */
function cubicBezier(x1: number, y1: number, x2: number, y2: number): (t: number) => number {
  const cx = 3 * x1;
  const bx = 3 * (x2 - x1) - cx;
  const ax = 1 - cx - bx;
  const cy = 3 * y1;
  const by = 3 * (y2 - y1) - cy;
  const ay = 1 - cy - by;
  const sampleX = (t: number) => ((ax * t + bx) * t + cx) * t;
  const sampleY = (t: number) => ((ay * t + by) * t + cy) * t;
  const dX = (t: number) => (3 * ax * t + 2 * bx) * t + cx;
  const solveX = (x: number): number => {
    let t = x;
    for (let i = 0; i < 8; i++) {
      const err = sampleX(t) - x;
      if (Math.abs(err) < 1e-5) return t;
      const d = dX(t);
      if (Math.abs(d) < 1e-6) break;
      t -= err / d;
    }
    let lo = 0;
    let hi = 1;
    t = x;
    while (lo < hi) {
      const err = sampleX(t) - x;
      if (Math.abs(err) < 1e-5) break;
      if (err > 0) hi = t;
      else lo = t;
      t = (lo + hi) / 2;
    }
    return t;
  };
  return (t: number) => (t <= 0 ? 0 : t >= 1 ? 1 : sampleY(solveX(t)));
}

export const EASING: Record<EasingName, (t: number) => number> = {
  linear: (t) => t,
  ease: cubicBezier(0.25, 0.1, 0.25, 1),
  'ease-in': cubicBezier(0.42, 0, 1, 1),
  'ease-out': cubicBezier(0, 0, 0.58, 1),
  'ease-in-out': cubicBezier(0.42, 0, 0.58, 1),
};

// ---------------------------------------------------------------------------
// value interpolation

const NUMBER_KEYS = new Set<keyof StyleDecls>(['opacity', 'borderRadius', 'borderWidth']);
const COLOR_KEYS = new Set<keyof StyleDecls>(['color', 'tint', 'backgroundColor', 'borderColor']);
const DIM_KEYS = new Set<keyof StyleDecls>([
  'width',
  'height',
  'top',
  'right',
  'bottom',
  'left',
  'marginTop',
  'marginRight',
  'marginBottom',
  'marginLeft',
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',
]);

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

function lerpColor(a: Color4, b: Color4, t: number): Color4 {
  return [lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t), lerp(a[3], b[3], t)];
}

/** Two transform lists interpolate iff they have the same functions in the same order. */
function transformsAlign(a: TransformDecl, b: TransformDecl): boolean {
  return a.length === b.length && a.every((f, i) => f.fn === b[i]!.fn);
}

function lerpTransform(a: TransformDecl, b: TransformDecl, t: number): TransformDecl {
  return a.map((fa, i): TransformFn => {
    const fb = b[i]!;
    switch (fa.fn) {
      case 'translate': {
        const g = fb as Extract<TransformFn, { fn: 'translate' }>;
        return {
          fn: 'translate',
          x: lerp(fa.x, g.x, t),
          y: lerp(fa.y, g.y, t),
          xPct: lerp(fa.xPct, g.xPct, t),
          yPct: lerp(fa.yPct, g.yPct, t),
        };
      }
      case 'scale': {
        const g = fb as Extract<TransformFn, { fn: 'scale' }>;
        return { fn: 'scale', x: lerp(fa.x, g.x, t), y: lerp(fa.y, g.y, t) };
      }
      case 'rotate': {
        const g = fb as Extract<TransformFn, { fn: 'rotate' }>;
        return { fn: 'rotate', rad: lerp(fa.rad, g.rad, t) };
      }
      case 'skew': {
        const g = fb as Extract<TransformFn, { fn: 'skew' }>;
        return { fn: 'skew', x: lerp(fa.x, g.x, t), y: lerp(fa.y, g.y, t) };
      }
      case 'rotateX': {
        const g = fb as Extract<TransformFn, { fn: 'rotateX' }>;
        return { fn: 'rotateX', rad: lerp(fa.rad, g.rad, t) };
      }
      case 'rotateY': {
        const g = fb as Extract<TransformFn, { fn: 'rotateY' }>;
        return { fn: 'rotateY', rad: lerp(fa.rad, g.rad, t) };
      }
      case 'translateZ': {
        const g = fb as Extract<TransformFn, { fn: 'translateZ' }>;
        return { fn: 'translateZ', z: lerp(fa.z, g.z, t) };
      }
      case 'perspective': {
        const g = fb as Extract<TransformFn, { fn: 'perspective' }>;
        return { fn: 'perspective', d: lerp(fa.d, g.d, t) };
      }
    }
  });
}

function lerpShadow(a: ShadowDecl, b: ShadowDecl, t: number): ShadowDecl {
  const out: ShadowDecl = {
    x: lerp(a.x, b.x, t),
    y: lerp(a.y, b.y, t),
    blur: lerp(a.blur, b.blur, t),
    spread: lerp(a.spread, b.spread, t),
    color: lerpColor(a.color, b.color, t),
  };
  if (a.inset) out.inset = true;
  return out;
}

/** Can this property's from→to pair be smoothly interpolated (else the engine snaps at t=1)? */
export function canInterpolate(key: keyof StyleDecls, from: unknown, to: unknown): boolean {
  if (from == null || to == null) return false;
  if (NUMBER_KEYS.has(key)) return typeof from === 'number' && typeof to === 'number';
  if (COLOR_KEYS.has(key)) return Array.isArray(from) && Array.isArray(to);
  if (DIM_KEYS.has(key)) return typeof from === 'number' && typeof to === 'number'; // plain px only
  if (key === 'transform') return transformsAlign(from as TransformDecl, to as TransformDecl);
  if (key === 'shadow') return Array.isArray(from) && Array.isArray(to) && from.length === to.length;
  return false;
}

/** Interpolate a supported style value at eased-progress `t`. Assumes canInterpolate returned true. */
export function lerpValue(
  key: keyof StyleDecls,
  from: unknown,
  to: unknown,
  t: number,
): StyleDecls[keyof StyleDecls] {
  if (NUMBER_KEYS.has(key) || DIM_KEYS.has(key)) return lerp(from as number, to as number, t) as never;
  if (COLOR_KEYS.has(key)) return lerpColor(from as Color4, to as Color4, t) as never;
  if (key === 'transform') return lerpTransform(from as TransformDecl, to as TransformDecl, t) as never;
  if (key === 'shadow') {
    const a = from as ShadowDecl[];
    const b = to as ShadowDecl[];
    return a.map((s, i) => lerpShadow(s, b[i]!, t)) as never;
  }
  return to as never;
}
